import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import i18n from '@medical/shared/i18n';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { useMedicationReminders } from '../contexts/MedicationReminderContext';
import {
  extractMedicationPayload,
  parseLegacyMedicationDetails,
  formatRoute,
  formatTimeSlots,
  formatFoodTiming,
  migrateOldTimingToNew,
  buildPrescriptionSummary,
  SCHEDULE_TYPE,
  getIntervalReminderTimes,
} from '@medical/shared/utils/medicationUtils';

const PageContainer = styled.div`
  max-width: 1150px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem 3rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem 2.5rem;
  }
`;

const Header = styled.header`
  margin-bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    gap: 0.4rem;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.4rem);
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.5px;

  @media (max-width: 480px) {
    font-size: clamp(1.5rem, 4vw, 1.75rem);
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: #475569;
  font-size: 1rem;
  max-width: 720px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const LatestNotice = styled.section`
  background: linear-gradient(135deg, #0F1F4D 0%, #0B63C5 100%);
  color: #ffffff;
  border-radius: 1.4rem;
  padding: clamp(1.75rem, 2vw, 2.25rem);
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 30px 60px -25px rgba(29, 78, 216, 0.45);

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border-radius: 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 0.75rem;
  }
`;

const NoticeTitle = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  
  h2 {
  margin: 0;
    font-size: clamp(1.4rem, 2vw, 1.7rem);
    font-weight: 700;
    letter-spacing: -0.3px;
  display: flex;
  align-items: center;
    gap: 0.65rem;
  }

  span {
    font-size: 0.95rem;
    color: rgba(226, 232, 240, 0.9);
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;


const HistoryHeading = styled.div`
  margin-top: 2.5rem;
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  @media (max-width: 768px) {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }

  h3 {
    margin: 0;
    font-size: clamp(1.2rem, 2vw, 1.4rem);
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.6rem;

    @media (max-width: 480px) {
      font-size: 1.125rem;
      gap: 0.5rem;
    }
  }

  span {
    color: #64748b;
    font-size: 0.95rem;

    @media (max-width: 768px) {
      font-size: 0.875rem;
    }

    @media (max-width: 480px) {
      font-size: 0.8125rem;
    }
  }
`;

const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const PrescriptionCard = styled.article`
  background: #ffffff;
  border-radius: 1.4rem;
  padding: clamp(1.75rem, 2vw, 2.25rem);
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: ${({ latest }) =>
    latest
      ? '0 24px 45px -20px rgba(29, 78, 216, 0.35)'
      : '0 12px 28px -20px rgba(15, 23, 42, 0.25)'};
  position: relative;
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: ${({ latest }) => (latest ? 'translateY(-4px)' : 'translateY(-3px)')};
    box-shadow: ${({ latest }) =>
      latest
        ? '0 28px 55px -20px rgba(29, 78, 216, 0.45)'
        : '0 18px 32px -20px rgba(15, 23, 42, 0.28)'};
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${({ variant }) =>
    variant === 'latest'
      ? 'linear-gradient(135deg, #1d4ed8, #1e3a8a)'
      : 'linear-gradient(135deg, #0f172a, #1e293b)'};
  color: #ffffff;
  box-shadow: 0 10px 20px -15px rgba(15, 23, 42, 0.35);
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.75rem;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    gap: 0.5rem;
  }
`;

const OrganizationInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;

  h3 {
  margin: 0;
    font-size: clamp(1.25rem, 2vw, 1.45rem);
    font-weight: 700;
    color: #0f172a;

    @media (max-width: 480px) {
      font-size: 1.125rem;
    }
  }

  span {
    font-size: 0.95rem;
    color: #64748b;

    @media (max-width: 480px) {
      font-size: 0.875rem;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const DownloadButton = styled.button`
  border: none;
  border-radius: 0.9rem;
  padding: 0.75rem 1.4rem;
  font-size: 0.88rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  color: #ffffff;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 16px 30px -20px rgba(4, 120, 87, 0.6);
  white-space: nowrap;
  
  &:hover {
    filter: brightness(0.98);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.625rem 1.25rem;
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    border-radius: 0.75rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`;

const DetailCard = styled.div`
  background: rgba(248, 250, 252, 0.95);
  border-radius: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(148, 163, 184, 0.15);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.625rem;
    border-radius: 0.5rem;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: #94a3b8;

  @media (max-width: 480px) {
    font-size: 0.6875rem;
  }
`;

const DetailValue = styled.strong`
  font-size: 0.95rem;
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  margin-top: 1.25rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.9);

  @media (max-width: 768px) {
    margin-top: 1rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    margin-top: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const MedicationTable = styled.table`
    width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 1rem;
  overflow: hidden;
  min-width: 600px;

  @media (max-width: 768px) {
    border-radius: 0.75rem;
    min-width: 500px;
  }

  @media (max-width: 480px) {
    border-radius: 0.5rem;
    min-width: 400px;
  }

  thead {
    background: #0f172a;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 0.75rem;
    display: table-header-group;

    @media (max-width: 480px) {
      font-size: 0.6875rem;
    }
  }

  tbody {
    display: table-row-group;
  }

  tr {
    display: table-row;
  }

  th,
  td {
    padding: 0.9rem 1rem;
    text-align: left;
    vertical-align: top;
    display: table-cell;

    @media (max-width: 768px) {
      padding: 0.75rem 0.5rem;
    }

    @media (max-width: 480px) {
      padding: 0.5rem 0.375rem;
      font-size: 0.75rem;
    }
  }

  tbody tr:nth-child(odd) {
    background: rgba(248, 250, 252, 0.65);
  }

  tbody tr:nth-child(even) {
    background: #ffffff;
  }

  td {
    font-size: 0.9rem;
    color: #1e293b;
    line-height: 1.5;
    word-break: break-word;

    @media (max-width: 768px) {
      font-size: 0.8125rem;
    }

    @media (max-width: 480px) {
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }
`;

const InstructionBox = styled.div`
  margin-top: 1.75rem;
  display: grid;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
    gap: 0.5rem;
  }
`;

const InstructionCard = styled.div`
  border-radius: 1rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: ${({ variant }) =>
    variant === 'patient'
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(59, 130, 246, 0.06))'
      : 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.06))'};
  color: #0f172a;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 0.5rem;
  }

  h4 {
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
  font-weight: 700;
    letter-spacing: 0.25px;
    color: ${({ variant }) => (variant === 'patient' ? '#1d4ed8' : '#047857')};
  display: flex;
  align-items: center;
  gap: 0.5rem;

    @media (max-width: 768px) {
      font-size: 0.875rem;
      margin: 0 0 0.375rem;
    }

    @media (max-width: 480px) {
      font-size: 0.8125rem;
      margin: 0 0 0.25rem;
    }
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;

    @media (max-width: 768px) {
      font-size: 0.8125rem;
      line-height: 1.5;
    }

    @media (max-width: 480px) {
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }
`;

const EmptyState = styled.div`
  border-radius: 1.5rem;
  padding: 3rem 1.5rem;
  background: #ffffff;
  text-align: center;
  border: 1px dashed rgba(148, 163, 184, 0.35);
  color: #475569;
  display: grid;
  gap: 0.75rem;

  h3 {
  margin: 0;
    font-size: clamp(1.1rem, 2vw, 1.25rem);
    font-weight: 700;
    color: #0f172a;
  }
`;

const EmptyIcon = styled.div`
  font-size: 2.5rem;
`;

const RemindersSection = styled.section`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
`;

const RemindersTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0c4a6e;
`;

const ReminderItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(14, 165, 233, 0.2);
  &:last-child {
    border-bottom: none;
  }
`;

const ReminderTimes = styled.span`
  font-size: 0.9rem;
  color: #0c4a6e;
`;

const TookItButton = styled.button`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 0.5rem;
  background: #0284c7;
  color: #fff;
  cursor: pointer;
  &:hover {
    background: #0369a1;
  }
`;

const FooterNote = styled.div`
  margin-top: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
    font-size: 0.75rem;
    gap: 0.375rem;
  }

  span {
    font-weight: 600;
    color: #0f172a;
  }
`;

const Medication = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const { nextReminder, reminderSchedule, markTaken } = useMedicationReminders();

  const handleTookIt = (medicationName) => {
    markTaken(medicationName);
    const promptMessage = t('medication.reminders.recordPainPrompt', {
      defaultValue: 'Record your pain now so your doctor can track the effect of your medication.',
    });
    const recordNowLabel = t('medication.reminders.recordPainNowButton', {
      defaultValue: 'Record pain now',
    });
    toast(
      (toastId) => (
        <div>
          <p style={{ margin: 0, fontWeight: 600 }}>{promptMessage}</p>
          <button
            type="button"
            onClick={() => {
              toast.dismiss(toastId.id);
              navigate('/body-diagram');
            }}
            style={{
              marginTop: '0.75rem',
              padding: '0.5rem 1rem',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {recordNowLabel}
          </button>
        </div>
      ),
      { duration: 15000 }
    );
  };

  // Helper function to format dates with localization
  const formatLocalizedDate = (date, options = {}) => {
    try {
      const lang = i18n.language || 'en';
      // Map i18n language codes to Intl locale codes
      const localeMap = {
        'en': 'en-US',
        'kn': 'kn-IN',
        'ta': 'ta-IN',
        'ml': 'ml-IN',
        'hi': 'hi-IN',
        'mr': 'mr-IN',
        'te': 'te-IN'
      };
      const locale = localeMap[lang] || 'en-US';

      const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };

      return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(date);
    } catch (e) {
      // Fallback to date-fns if Intl fails
      return format(date, 'MMM d, yyyy h:mm a');
    }
  };
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const isInitialLoad = useRef(true);
  const markedAsReadRef = useRef(new Set());
  const lastCountRef = useRef(0);
  const fetchRef = useRef(null);
  
  useEffect(() => {
    fetchRef.current = async () => {
      if (!user || !token) {
        setLoading(false);
        return;
      }
      
      try {
        if (isInitialLoad.current) {
          setLoading(true);
        }
        
        const response = await axios.get('/api/messages', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.data?.success) {
          setPrescriptions([]);
          return;
        }

        const meds = (response.data.messages || [])
          .filter((message) => message.type === 'medication')
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((message) => {
            const { payload, summary } = extractMedicationPayload(message.message);

            const fallbackDoctor =
              message.sender?.firstName && message.sender?.lastName
                ? `Dr. ${message.sender.firstName} ${message.sender.lastName}`
                : t('medication.labels.yourDoctor');

            if (payload) {
              return {
                id: message._id,
                createdAt: message.createdAt,
                doctorName: payload.doctor?.name || fallbackDoctor,
                payload,
                summary,
                type: 'structured',
              };
            }

            return {
              id: message._id,
              createdAt: message.createdAt,
              doctorName: fallbackDoctor,
              summary,
              legacy: parseLegacyMedicationDetails(message.message, message.subject),
              rawMessage: message.message,
              type: 'legacy',
            };
          });

        if (
          !isInitialLoad.current &&
          meds.length > lastCountRef.current &&
          lastCountRef.current > 0
        ) {
          toast.success(t('medication.success.newPrescription'), { duration: 4000 });
        }

        lastCountRef.current = meds.length;
        setPrescriptions(meds);
      } catch (error) {
        console.error('Failed to load prescriptions:', error);
        if (isInitialLoad.current) {
          toast.error(t('medication.errors.loadFailed'));
        }
        setPrescriptions([]);
      } finally {
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          setLoading(false);
        }
      }
    };
  }, [token, user]);

  useEffect(() => {
    if (!fetchRef.current) return;
    fetchRef.current();

    const intervalId = setInterval(() => {
      fetchRef.current?.();
    }, 10000);

    const handler = () => {
      fetchRef.current?.();
    };
    window.addEventListener('medicationSent', handler);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('medicationSent', handler);
    };
  }, []);

  useEffect(() => {
    if (!token || prescriptions.length === 0) return;
    
    const unreadIds = prescriptions
      .map((item) => item.id)
      .filter((id) => id && !markedAsReadRef.current.has(id));
    
    if (unreadIds.length === 0) return;
    
    const timeout = setTimeout(async () => {
      try {
        await Promise.all(
          unreadIds.map((id) =>
            axios
              .put(
                `/api/messages/${id}/read`,
                {},
                {
                headers: {
                    Authorization: `Bearer ${token}`,
                  },
                },
              )
              .catch(() => {}),
          ),
        );

        unreadIds.forEach((id) => markedAsReadRef.current.add(id));
          window.dispatchEvent(new Event('refreshUnreadCount'));
      } catch (error) {
        console.warn('Failed to mark prescriptions as read:', error);
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [prescriptions, token]);

  const handleDownload = async (prescription) => {
    if (!prescription) {
      toast.error(t('medication.errors.noPrescription'));
      return;
    }

    // Get English translations for PDF generation
    const tEnglish = i18n.getFixedT('en');

    if (prescription.type === 'structured' && prescription.payload) {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const payload = prescription.payload;
      const lineHeight = 18;
      const pageWidth = doc.internal.pageSize.getWidth();
      const safeMargin = 48;
      const contentWidth = pageWidth - safeMargin * 2;
      let cursor = safeMargin;

      const ensureSpace = (height = lineHeight) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        if (cursor + height > pageHeight - safeMargin) {
          doc.addPage();
          cursor = safeMargin;
        }
      };

      const writeLine = (text, options = {}) => {
        const {
          size = 11,
          weight = 'normal',
          color = '#111827',
          align = 'left',
          maxWidth = contentWidth,
        } = options;

        doc.setFont('Helvetica', weight === 'bold' ? 'bold' : weight === 'semibold' ? 'bold' : 'normal');
        doc.setFontSize(size);
        doc.setTextColor(color);

        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach((line) => {
          ensureSpace(lineHeight);
          doc.text(line, safeMargin + (align === 'center' ? contentWidth / 2 : 0), cursor, {
            align,
          });
          cursor += lineHeight;
        });
      };

      const writeSpacing = (amount = lineHeight / 2) => {
        cursor += amount;
      };

      // Load images and add logos at the top corners
      const logo1Size = 65; // Size for logo.png (left)
      const logo2Size = 65; // Size for logo 2 (right)
      const logoTopMargin = safeMargin;
      const logoLeftX = safeMargin;
      const logoRightX = pageWidth - safeMargin - logo2Size;

      // Helper function to load image and get its dimensions
      const loadImageWithDimensions = (imagePath) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              const dataURL = canvas.toDataURL('image/png');
              const formatMatch = /^data:image\/(png|jpe?g)/i.exec(dataURL);
              resolve({
                format: formatMatch && /jpe?g/i.test(formatMatch[1]) ? 'JPEG' : 'PNG',
                dataURL,
                width: img.width,
                height: img.height,
                aspectRatio: img.width / img.height
              });
            } catch (error) {
              reject(error);
            }
          };
          img.onerror = () => reject(new Error(`Failed to load image: ${imagePath}`));
          const isAbsolute = /^(https?:|data:|blob:|\/\/)/i.test(imagePath);
          if (/^(https?:)?\/\//i.test(imagePath)) {
            try {
              if (new URL(imagePath, window.location.href).origin !== window.location.origin) {
                img.crossOrigin = 'anonymous';
              }
            } catch (e) {
              img.crossOrigin = 'anonymous';
            }
          }
          const isPublicAsset = !isAbsolute && !imagePath.startsWith('/uploads/');
          img.src = isPublicAsset && process.env.PUBLIC_URL
            ? `${process.env.PUBLIC_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`
            : imagePath;
        });
      };

      // Load and add logos with timeout and highlighting, preserving aspect ratio
      const loadLogoWithTimeout = async (imagePath, position, maxSize, timeout = 3000, alignRight = false) => {
        try {
          const imageData = await Promise.race([
            loadImageWithDimensions(imagePath),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
          ]);
          
          // Calculate dimensions preserving aspect ratio
          let logoWidth, logoHeight;
          if (imageData.aspectRatio >= 1) {
            // Landscape or square
            logoWidth = maxSize;
            logoHeight = maxSize / imageData.aspectRatio;
          } else {
            // Portrait
            logoHeight = maxSize;
            logoWidth = maxSize * imageData.aspectRatio;
          }
          
          // Add logo image with preserved aspect ratio
          const x = alignRight ? position + maxSize - logoWidth : position;
          doc.addImage(imageData.dataURL, imageData.format, x, logoTopMargin, logoWidth, logoHeight);
          
          return { width: logoWidth, height: logoHeight };
        } catch (error) {
          console.warn(`Could not load ${imagePath}:`, error);
          return null;
        }
      };

      const hospitalLogoUrl = (user?.hospitalLogoUrl || '').trim();
      const loadLeftLogo = async () => {
        if (hospitalLogoUrl) {
          const result = await loadLogoWithTimeout(hospitalLogoUrl, logoLeftX, logo1Size);
          if (result) return result;
        }
        return loadLogoWithTimeout('/logo-mark.jpg', logoLeftX, logo1Size);
      };

      const logoResults = await Promise.all([
        loadLeftLogo(),
        loadLogoWithTimeout('/nabh-logo.png', logoRightX, logo2Size, 3000, true)
      ]);

      // Calculate the maximum height from both logos for spacing
      const maxLogoHeight = logoResults.reduce((max, result) => {
        return result ? Math.max(max, result.height) : max;
      }, Math.max(logo1Size, logo2Size));

      // Start header content below logos
      cursor = logoTopMargin + maxLogoHeight + lineHeight * 1.2;

      const headerName = (
        user?.hospitalName?.trim() ||
        payload.organizationName?.trim() ||
        'Anuvedana'
      ).toUpperCase();
      const headerAddress = (user?.hospitalAddress || payload.organizationAddress || '').trim();
      const headerContact = (payload.organizationContact || '').trim();

      doc.setFontSize(18);
      doc.setFont('Helvetica', 'bold');
      const headerNameLines = doc.splitTextToSize(headerName, contentWidth);
      headerNameLines.forEach((line) => {
        doc.text(line, pageWidth / 2, cursor, { align: 'center' });
        cursor += lineHeight;
      });

      if (headerAddress) {
        doc.setFontSize(9);
        doc.setFont('Helvetica', 'normal');
        doc.splitTextToSize(headerAddress, contentWidth).forEach((line) => {
          doc.text(line, pageWidth / 2, cursor, { align: 'center' });
          cursor += lineHeight * 0.85;
        });
      }
      if (headerContact) {
        doc.setFontSize(9);
        doc.setFont('Helvetica', 'normal');
        doc.text(headerContact, pageWidth / 2, cursor, { align: 'center' });
        cursor += lineHeight * 0.85;
      }
      cursor += lineHeight;

      writeSpacing(lineHeight / 2);

      // MEDICINE PRESCRIPTION / OPD REPORT (right above the separation line, underlined)
      doc.setFontSize(14);
      doc.setFont('Helvetica', 'bold');
      const prescriptionText = 'MEDICINE PRESCRIPTION / OPD REPORT';
      doc.text(prescriptionText, pageWidth / 2, cursor, {
        align: 'center',
      });
      // Draw underline for MEDICINE PRESCRIPTION / OPD REPORT
      const textWidth = doc.getTextWidth(prescriptionText);
      const startX = (pageWidth - textWidth) / 2;
      doc.setLineWidth(0.7);
      doc.setDrawColor(0, 0, 0);
      doc.line(startX, cursor + 3, startX + textWidth, cursor + 3);
      cursor += lineHeight * 1.2;

      // Separation line
      doc.setLineWidth(0.7);
      doc.setDrawColor(55, 65, 81);
      doc.line(safeMargin, cursor, pageWidth - safeMargin, cursor);
      cursor += lineHeight;

      const patientName = payload.patient?.name || user?.fullName || 'N/A';
      const patientId = payload.patient?.id || user?.patientId || 'N/A';
      // Use English date format for PDF
      const issuedAt = payload.issuedAt
        ? format(new Date(payload.issuedAt), 'MMM dd, yyyy • h:mm a')
        : format(new Date(prescription.createdAt), 'MMM dd, yyyy • h:mm a');

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`${tEnglish('medication.labels.patient')}: ${patientName}`, safeMargin, cursor);
      doc.text(`${tEnglish('medication.labels.issuedOn')}: ${issuedAt}`, pageWidth / 2 + 20, cursor);
      cursor += lineHeight;

      doc.setFont('Helvetica', 'normal');
      // Doctor name and registration number
      const doctorNameParts = [
        payload.doctor?.name || tEnglish('medication.labels.treatingPhysician'),
        payload.doctor?.registrationNumber ? `${tEnglish('medication.labels.reg')} ${payload.doctor.registrationNumber}` : null,
      ]
        .filter(Boolean)
        .join(' • ');
      doc.text(`${tEnglish('medication.labels.doctor')}: ${doctorNameParts}`, safeMargin, cursor);
      cursor += lineHeight;

      // Contact number on next line
      if (payload.doctor?.phone) {
        doc.text(`${tEnglish('medication.labels.contact')} ${payload.doctor.phone}`, safeMargin, cursor);
        cursor += lineHeight;
      }
      cursor += lineHeight * 0.2;

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(tEnglish('medication.labels.medication'), safeMargin, cursor);
      cursor += lineHeight;

      const tableBody = (payload.medications || []).map((item, index) => {
        const route = item.route ? formatRoute(item.route) : '—';
        // Use new structure with migration fallback
        const timing = migrateOldTimingToNew(item);
        const timeSlots = timing.timeSlots && timing.timeSlots.length > 0 
          ? formatTimeSlots(timing.timeSlots, tEnglish) 
          : '—';
        const foodTiming = timing.foodTiming 
          ? formatFoodTiming(timing.foodTiming, tEnglish) 
          : tEnglish('medication.labels.asDirected');
        return [
          index + 1,
          [item.name, item.form, item.strength].filter(Boolean).join(' • ') || '—',
          [item.dose, item.quantity].filter(Boolean).join(' • ') || '—',
          route,
          timeSlots,
          foodTiming,
          item.duration || '—',
        ];
      });

      autoTable(doc, {
        startY: cursor,
        head: [['#', tEnglish('medication.labels.medication'), tEnglish('medication.labels.doseQty'), 'Route', 'Time of Day', tEnglish('medication.labels.food'), tEnglish('medication.labels.duration')]],
        body: tableBody,
        margin: { left: safeMargin, right: safeMargin },
        styles: {
          font: 'helvetica',
          fontSize: 9,
          cellPadding: 4,
          lineColor: [148, 163, 184],
          lineWidth: 0.5,
          halign: 'left',
          valign: 'top',
        },
        headStyles: {
          fillColor: [15, 23, 42],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
        },
        columnStyles: {
          0: { cellWidth: 25, halign: 'center' },
          1: { cellWidth: 130 },
          2: { cellWidth: 75 },
          3: { cellWidth: 35 },
          4: { cellWidth: 80 },
          5: { cellWidth: 70 },
          6: { cellWidth: 60 },
          7: { cellWidth: 60 },
        },
        bodyStyles: {
          textColor: [17, 24, 39],
          fontSize: 8,
        },
        tableWidth: contentWidth,
      });

      cursor = doc.lastAutoTable.finalY + lineHeight;

      const filename = `Prescription_${payload.patient?.id || user?.patientId || 'patient'}_${format(
        new Date(prescription.createdAt),
        'yyyyMMdd_HHmm',
      )}.pdf`;
      doc.save(filename);
      toast.success(t('medication.success.downloaded'));
      return;
    }

    const summary = buildPrescriptionSummary(
      {
        organizationName: '',
        patient: { name: user?.fullName, id: user?.patientId },
        doctor: { name: prescription.doctorName },
        issuedAt: prescription.createdAt,
        medications: [],
        additionalInstructions: prescription.summary || prescription.rawMessage || t('medication.title'),
      },
      { includeInstructions: true },
    );

    const blob = new Blob([summary], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `Prescription_${user?.patientId || 'patient'}_${format(
      new Date(prescription.createdAt),
      'yyyyMMdd_HHmm',
    )}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    toast.success(t('medication.success.downloaded'));
  };

  const latestPrescription = prescriptions[0];
  const historyPrescriptions = prescriptions.slice(1);

  const renderStructuredPrescription = (prescription, latest = false) => {
    const payload = prescription.payload;
    const meds = payload.medications || [];
    const issuedAt = payload.issuedAt ? new Date(payload.issuedAt) : new Date(prescription.createdAt);
    const validUntil = payload.validUntil ? new Date(payload.validUntil) : null;

    return (
      <PrescriptionCard key={prescription.id} latest={latest}>
        <CardHeader>
          <OrganizationInfo>
            <div>
              <h3>{payload.organizationName || t('medication.title')}</h3>
              {(payload.organizationAddress || payload.organizationContact) && (
                <span>
                  {payload.organizationAddress}
                  {payload.organizationAddress && payload.organizationContact ? ' • ' : ''}
                  {payload.organizationContact}
                </span>
              )}
                    </div>
            <DownloadButton onClick={() => handleDownload(prescription)}>
              {t('medication.download.formal')}
                </DownloadButton>
          </OrganizationInfo>
          <Badge variant={latest ? 'latest' : 'default'}>
            {latest ? t('medication.current.badge') : t('medication.history.badge')}
          </Badge>
        </CardHeader>

        <DetailsGrid>
          <DetailCard>
            <DetailLabel>{t('medication.labels.patient')}</DetailLabel>
            <DetailValue>{payload.patient?.name || user?.fullName || '—'}</DetailValue>
            {(payload.patient?.id || user?.patientId) && (
              <span>{t('medication.labels.id')} {payload.patient?.id || user?.patientId}</span>
            )}
          </DetailCard>
        <DetailCard>
          <DetailLabel>{t('medication.labels.doctor')}</DetailLabel>
          <DetailValue>{payload.doctor?.name || prescription.doctorName || '—'}</DetailValue>
          {[payload.doctor?.registrationNumber, payload.doctor?.phone]
            .filter(Boolean)
            .map((item) => (
              <span key={item}>{item}</span>
            ))}
        </DetailCard>
          <DetailCard>
            <DetailLabel>{t('medication.labels.issuedOn')}</DetailLabel>
            <DetailValue>{formatLocalizedDate(issuedAt)}</DetailValue>
          </DetailCard>
        </DetailsGrid>

        {meds.length > 0 && (
          <TableWrapper>
            <MedicationTable>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('medication.labels.medication')}</th>
                  <th>{t('medication.labels.doseQty')}</th>
                  <th>Route</th>
                  <th>Time of Day</th>
                  <th>{t('medication.labels.food')}</th>
                  <th>{t('medication.labels.duration')}</th>
                  <th>{t('medication.labels.notes')}</th>
                </tr>
              </thead>
              <tbody>
                {meds.map((item, idx) => {
                  const route = item.route ? formatRoute(item.route) : '—';
                  const timing = migrateOldTimingToNew(item);
                  let timeSlotsDisplay = '—';
                  if (timing.scheduleType === SCHEDULE_TYPE.INTERVAL && (timing.intervalHours || timing.firstDoseTime)) {
                    const times = getIntervalReminderTimes(timing.intervalHours || 4, timing.firstDoseTime || '08:00');
                    timeSlotsDisplay = t('medication.labels.everyXHours', { defaultValue: 'Every {{hours}}h at' }, { hours: timing.intervalHours || 4 })
                      + ' ' + times.map(({ hour, minute }) => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`).join(', ');
                  } else if (timing.timeSlots && timing.timeSlots.length > 0) {
                    timeSlotsDisplay = formatTimeSlots(timing.timeSlots, t);
                  }
                  const foodTiming = timing.foodTiming 
                    ? formatFoodTiming(timing.foodTiming, t) 
                    : t('medication.labels.asDirected');
                  return (
                    <tr key={`${prescription.id}-med-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong>{item.name || t('medication.labels.medication')}</strong>
                        <div style={{ color: '#64748b', marginTop: '0.25rem' }}>
                          {[item.form, item.strength].filter(Boolean).join(' • ')}
                        </div>
                      </td>
                      <td>{[item.dose, item.quantity].filter(Boolean).join(' • ') || t('medication.labels.asPrescribed')}</td>
                      <td>{route}</td>
                      <td>{timeSlotsDisplay}</td>
                      <td>{foodTiming}</td>
                      <td>{item.duration || '—'}</td>
                      <td>{item.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </MedicationTable>
          </TableWrapper>
        )}

        {(payload.additionalInstructions || payload.pharmacyNotes) && (
          <InstructionBox>
            {payload.additionalInstructions && (
              <InstructionCard variant="patient">
                <h4>{t('medication.labels.patientInstructions')}</h4>
                <p>{payload.additionalInstructions}</p>
              </InstructionCard>
            )}
            {payload.pharmacyNotes && (
              <InstructionCard variant="pharmacy">
                <h4>{t('medication.labels.pharmacyNotes')}</h4>
                <p>{payload.pharmacyNotes}</p>
              </InstructionCard>
            )}
          </InstructionBox>
        )}
      </PrescriptionCard>
    );
  };

  const renderLegacyPrescription = (prescription) => (
    <PrescriptionCard key={prescription.id}>
      <CardHeader>
        <OrganizationInfo>
          <div>
            <h3>{t('medication.legacy.title')}</h3>
            <span>{t('medication.legacy.subtitle')}</span>
                          </div>
          <DownloadButton onClick={() => handleDownload(prescription)}>
            {t('medication.download.text')}
          </DownloadButton>
        </OrganizationInfo>
        <Badge>{t('medication.legacy.badge')}</Badge>
      </CardHeader>

      <DetailsGrid>
        <DetailCard>
          <DetailLabel>{t('medication.labels.doctor')}</DetailLabel>
          <DetailValue>{prescription.doctorName}</DetailValue>
        </DetailCard>
        <DetailCard>
          <DetailLabel>{t('medication.labels.issuedOn')}</DetailLabel>
          <DetailValue>{formatLocalizedDate(new Date(prescription.createdAt))}</DetailValue>
        </DetailCard>
      </DetailsGrid>

      <InstructionBox>
        {prescription.legacy ? (
          <>
            <InstructionCard variant="patient">
              <h4>{t('medication.labels.medication')}</h4>
              <p>
                <strong>{t('common.name')}:</strong> {prescription.legacy.name}
                {prescription.legacy.dosage && (
                  <>
                    <br />
                    <strong>{t('medication.labels.doseQty')}:</strong> {prescription.legacy.dosage}
                          </>
                        )}
                {prescription.legacy.frequency && (
                  <>
                    <br />
                    <strong>{t('medication.labels.timing')}:</strong> {prescription.legacy.frequency}
                  </>
                )}
              </p>
            </InstructionCard>
            {prescription.legacy.instructions && (
              <InstructionCard variant="pharmacy">
                <h4>{t('medication.labels.pharmacyNotes')}</h4>
                <p>{prescription.legacy.instructions}</p>
              </InstructionCard>
          )}
        </>
      ) : (
          <InstructionCard variant="patient">
            <h4>{t('medication.labels.notes')}</h4>
            <p>{prescription.summary || t('messages.empty.text')}</p>
          </InstructionCard>
        )}
      </InstructionBox>
    </PrescriptionCard>
  );

  if (loading) {
    return <LoadingSpinner text={t('medication.loading')} />;
  }

  return (
    <PageContainer>
      <Header>
        <Title>{t('medication.title')}</Title>
        <Subtitle>
          {t('medication.subtitle')}
        </Subtitle>
      </Header>

      {prescriptions.length === 0 ? (
        <EmptyState>
          <EmptyIcon>💊</EmptyIcon>
          <h3>{t('medication.empty.title')}</h3>
          <p>{t('medication.empty.message')}</p>
        </EmptyState>
      ) : (
        <>
          {latestPrescription && (
            <>
              <LatestNotice>
                <NoticeTitle>
                  <h2>{t('medication.current.title')}</h2>
                  <span>
                    {t('medication.current.lastUpdated')}{' '}
                    {formatLocalizedDate(new Date(latestPrescription.createdAt))}
                  </span>
                </NoticeTitle>
              </LatestNotice>
              {reminderSchedule.length > 0 && (
                <RemindersSection>
                  <RemindersTitle>
                    {t('medication.reminders.title', { defaultValue: 'Reminders' })}
                    {nextReminder && (
                      <span style={{ fontWeight: 500, fontSize: '0.9rem', display: 'block', marginTop: '0.35rem' }}>
                        {t('medication.reminders.next', { defaultValue: 'Next' })}: {nextReminder.label}
                      </span>
                    )}
                  </RemindersTitle>
                  {reminderSchedule.map((s) => {
                    const timesStr = s.times
                      .map(({ hour, minute }) => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
                      .join(', ');
                    return (
                      <ReminderItem key={`${s.prescriptionId}-${s.medicationIndex}`}>
                        <ReminderTimes>
                          <strong>{s.medicationName}</strong> — {t('medication.reminders.at', { defaultValue: 'at' })} {timesStr}
                        </ReminderTimes>
                        <TookItButton type="button" onClick={() => handleTookIt(s.medicationName)}>
                          {t('medication.reminders.iTookIt', { defaultValue: 'I took it' })}
                        </TookItButton>
                      </ReminderItem>
                    );
                  })}
                </RemindersSection>
              )}
              {latestPrescription.type === 'structured'
                ? renderStructuredPrescription(latestPrescription, true)
                : renderLegacyPrescription(latestPrescription)}
            </>
          )}

          {historyPrescriptions.length > 0 && (
            <>
              <HistoryHeading>
                <h3>{t('medication.history.title')}</h3>
                <span>{t('medication.history.subtitle')}</span>
              </HistoryHeading>
              <CardsStack>
                {historyPrescriptions.map((item) =>
                  item.type === 'structured'
                    ? renderStructuredPrescription(item)
                    : renderLegacyPrescription(item),
                )}
              </CardsStack>
            </>
          )}
        </>
      )}

      <FooterNote>
        <span>{t('medication.footer.needAssistance')}</span>
        {t('medication.footer.shareNote')}
      </FooterNote>
    </PageContainer>
  );
};

export default Medication;


